import React, { useState, useEffect } from 'react';
import { Star, X, Send, Trophy, TrendingUp, TrendingDown, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

// Always use tradepayflow.emergent.host for bonbot.biz custom domain
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

// Star Rating Component
const StarRating = ({ rating, setRating, readonly = false }) => {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && setRating(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => !readonly && setHover(0)}
          className={`transition-transform ${!readonly && 'hover:scale-110 cursor-pointer'}`}
        >
          <Star
            className={`w-8 h-8 ${
              (hover || rating) >= star
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-500'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// Review Request Popup Modal
export const ReviewRequestModal = ({ isOpen, onClose, reviewRequest, onSubmit }) => {
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !reviewRequest) return null;

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    if (reviewText.trim().length < 10) {
      toast.error('Please write at least 10 characters');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('guestToken');
      const response = await fetch(`${API}/user/submit-review?token=${token}&request_id=${reviewRequest.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          review_text: reviewText.trim()
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Review submitted successfully!');
        onSubmit && onSubmit();
        onClose();
      } else {
        toast.error(data.message || 'Failed to submit review');
      }
    } catch (err) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const trades = reviewRequest.trades_to_review || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl overflow-hidden border border-white/10">
        {/* Header */}
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-6 py-4 text-center">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
            <Star className="w-8 h-8 text-white fill-white" />
          </div>
          <h2 className="text-xl font-bold text-white">Share Your Experience</h2>
          <p className="text-white/80 text-sm mt-1">We'd love to hear about your trading results!</p>
        </div>

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 transition-colors"
        >
          <X className="w-5 h-5 text-white" />
        </button>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Recent Trades Summary */}
          {trades.length > 0 && (
            <div className="bg-white/5 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-yellow-400" />
                Your Recent Trades to Review
              </h3>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {trades.map((trade, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-2 rounded ${
                      trade.result === 'win' ? 'bg-green-500/10' : 
                      trade.result === 'loss' ? 'bg-red-500/10' : 'bg-gray-500/10'
                    }`}
                  >
                    <span className="text-sm text-white">{trade.asset}</span>
                    <span className={`flex items-center gap-1 text-sm font-medium ${
                      trade.result === 'win' ? 'text-green-400' : 
                      trade.result === 'loss' ? 'text-red-400' : 'text-gray-400'
                    }`}>
                      {trade.result === 'win' ? <TrendingUp className="w-3 h-3" /> : 
                       trade.result === 'loss' ? <TrendingDown className="w-3 h-3" /> : null}
                      {trade.result?.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rating */}
          <div className="text-center">
            <p className="text-sm text-gray-400 mb-2">How would you rate your experience?</p>
            <div className="flex justify-center">
              <StarRating rating={rating} setRating={setRating} />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {rating === 0 ? 'Select a rating' :
               rating === 1 ? 'Poor' :
               rating === 2 ? 'Fair' :
               rating === 3 ? 'Good' :
               rating === 4 ? 'Very Good' : 'Excellent!'}
            </p>
          </div>

          {/* Review Text */}
          <div>
            <label className="text-sm text-gray-300 mb-2 block">Your Review</label>
            <Textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your experience with Bonbot signals..."
              className="bg-white/5 border-white/20 text-white placeholder:text-gray-500 min-h-[100px]"
              maxLength={500}
            />
            <p className="text-xs text-gray-500 mt-1 text-right">{reviewText.length}/500</p>
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-bold py-4"
          >
            {submitting ? (
              <>Submitting...</>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Submit Review
              </>
            )}
          </Button>

          <p className="text-xs text-center text-gray-500">
            Your review will be visible after admin approval
          </p>
        </div>
      </div>
    </div>
  );
};

// User's Own Reviews Display
export const UserReviewsList = ({ reviews }) => {
  if (!reviews || reviews.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <Star className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p>You haven't submitted any reviews yet</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => (
        <div 
          key={review.id}
          className="bg-white/5 rounded-lg p-4 border border-white/10"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-4 h-4 ${
                    star <= review.rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-600'
                  }`}
                />
              ))}
            </div>
            <span className={`text-xs px-2 py-1 rounded ${
              review.status === 'approved' ? 'bg-green-500/20 text-green-400' :
              review.status === 'pending_approval' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {review.status === 'approved' ? '✓ Published' :
               review.status === 'pending_approval' ? '⏳ Pending Review' :
               '✗ Rejected'}
            </span>
          </div>
          <p className="text-gray-300 text-sm">{review.review_text}</p>
          <p className="text-xs text-gray-500 mt-2">
            Submitted: {new Date(review.submitted_at).toLocaleDateString()}
          </p>
        </div>
      ))}
    </div>
  );
};

// Public Reviews Display for Homepage (shows approved reviews only)
export const PublicReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API}/public/reviews`);
        const data = await response.json();
        if (data.success) {
          setReviews(data.reviews || []);
        }
      } catch (err) {
        console.error('Failed to fetch reviews');
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-yellow-400 mx-auto"></div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return null; // Don't show section if no reviews
  }

  return (
    <div className="w-full max-w-4xl mx-auto mt-12 mb-8 px-4">
      <div className="text-center mb-6">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
          <h2 className="text-xl font-bold text-white">What Our Users Say</h2>
          <Star className="w-6 h-6 text-yellow-400 fill-yellow-400" />
        </div>
        <p className="text-sm text-gray-400">Real reviews from real traders</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reviews.slice(0, 6).map((review) => (
          <div
            key={review.id}
            className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl p-4 hover:bg-white/10 transition-colors"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                {review.user_name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <p className="text-white font-medium text-sm">{review.user_name}</p>
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-3 h-3 ${
                        star <= review.rating
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-600'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
            <p className="text-gray-300 text-sm line-clamp-3">{review.review_text}</p>
            <p className="text-xs text-gray-500 mt-2">
              {new Date(review.approved_at).toLocaleDateString()}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Hook to check for pending review requests
export const useReviewRequest = () => {
  const [reviewRequest, setReviewRequest] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);

  useEffect(() => {
    const checkForReviewRequest = async () => {
      const token = localStorage.getItem('guestToken');
      if (!token) return;

      try {
        const response = await fetch(`${API}/user/review-request?token=${token}`);
        const data = await response.json();
        if (data.success && data.has_pending_request) {
          setReviewRequest(data.review_request);
          // Show the modal after a short delay
          setTimeout(() => setShowReviewModal(true), 2000);
        }
      } catch (err) {
        // Ignore errors
      }
    };

    checkForReviewRequest();
  }, []);

  return {
    reviewRequest,
    showReviewModal,
    setShowReviewModal,
    clearReviewRequest: () => {
      setReviewRequest(null);
      setShowReviewModal(false);
    }
  };
};

export default ReviewRequestModal;
